import objectPath from "object-path";
import ProfileType from "~/enums/ProfileType";
import Api from "~/api";
import uuid from "uuid/v4";
import * as immutable from "object-path-immutable";
import FetchState from "~/enums/FetchState";
import ContentDisposition from "content-disposition";
import config from "~/config";
import MatchSourceEntity from "~/entities/MatchSourceEntity";
import {fetchBackendSelection} from "~/actions/util/backend-selection";
import BackendSelection from "~/util/BackendSelection";
import IndexDocumentType from "~/enums/IndexDocumentType";

export const CREATE_CONTEXT = "matching/CREATE_CONTEXT";
export const NEW_MATCH_STARTED = "matching/NEW_MATCH_STARTED";
export const PAGE_REQUESTED = "matching/PAGE_REQUESTED";
export const PAGE_RECEIVED = "matching/PAGE_RECEIVED";
export const RESULT_ANNOTATIONS_RECEIVED = "matching/RESULT_ANNOTATIONS_RECEIVED";
export const PAGE_REQUEST_ERROR = "matching/PAGE_REQUEST_ERROR";
export const BACKEND_SELECTION_REQUESTED = "matching/BACKEND_SELECTION_REQUESTED";
export const BACKEND_SELECTION_RECEIVED = "matching/BACKEND_SELECTION_RECEIVED";
export const BACKEND_SELECTION_REQUEST_ERROR = "matching/BACKEND_SELECTION_REQUEST_ERROR";
export const SET_QUERY = "matching/SET_QUERY";
export const SET_NEXT_REQUEST = "matching/SET_NEXT_REQUEST";
export const SET_LAST_REQUEST = "matching/SET_LAST_REQUEST";
export const CLEAR_NEXT_REQUEST = "matching/CLEAR_NEXT_REQUEST";
export const RESET_NEXT_REQUEST = "matching/RESET_NEXT_REQUEST";
export const SET_SELECTION = "matching/SET_SELECTION";
export const SET_DISPLAYED_PAGE = "matching/SET_DISPLAYED_PAGE";
export const SET_MATCH_SOURCE_ENTITY = "matching/SET_MATCH_SOURCE_ENTITY";
export const MATCH_SOURCE_ENTITY_LOADED = "matching/MATCH_SOURCE_ENTITY_LOADED";
export const SET_INDEX_NEXT_REQUEST = "matching/SET_INDEX_NEXT_REQUEST";
export const SET_INDEX_LAST_REQUEST = "matching/SET_INDEX_LAST_REQUEST";
export const SET_SORT_MODE = "matching/SET_SORT_MODE";
export const SET_SELECTION_STATUS = "matching/SET_SELECTION_STATUS";
export const UPDATE_UI_STATE = "matching/UPDATE_UI_STATE";
export const REPLACE_MATCH = "matching/REPLACE_MATCH";

const BACKEND_SELECTION_SPACING_MILLISECONDS = 30 * 1000;

export function createMatchingContext(id, configurationFn) {
    return (dispatch, getState) => {
        const configuration = configurationFn(getState());

        dispatch({
            type: CREATE_CONTEXT,
            id,
            configurationFn,
            configuration,
        });
    };
}

export function actionsForMatchingContext(contextId) {
    const actions = {
        newMatch,
        updateMatchForIndex,
        requestPage,
        makeSelection,
        setMatchSourceEntity,
        setQuery,
        setNextRequest,
        setIndexNextRequest,
        clearNextRequest,
        resetNextRequest,
        setSelection,
        downloadMatchResults,
        setSortMode,
        setSelectionStatus,
        updateUiState,
        selectAllFromApi,
    };

    return Object.keys(actions).reduce((mappedActions, key) => {
        mappedActions[key] = actions[key].bind(undefined, contextId);
        return mappedActions;
    }, {});
}

export function replaceMatch(contextId, indexName, matchId, data) {
    return {
        type: REPLACE_MATCH,
        contextId,
        indexName,
        matchId,
        data,
    };
}

function newMatch(contextId) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const configuration = matchingContext.configurationFn(getState());
        const promises = [];

        dispatch(newMatchStarted(contextId));

        for (let i = 0; i < configuration.indices.length; i++) {
            const index = configuration.indices[i];
            if (index.disabled) continue;

            const indexState = matchingContext.results[index.name];

            promises.push(
                startMatchForIndex(
                    dispatch,
                    getState,
                    configuration,
                    contextId,
                    matchingContext,
                    index,
                    indexState.nextRequest,
                    matchingContext.nextRequest
                )
            );
        }

        dispatch(setLastRequest(contextId, matchingContext.nextRequest));
        return Promise.all(promises);
    };
}

function updateMatchForIndex(contextId, index) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const indexState = matchingContext.results[index.name];
        const configuration = matchingContext.configurationFn(getState());

        dispatch(newMatchStarted(contextId, index));

        return startMatchForIndex(
            dispatch,
            getState,
            configuration,
            contextId,
            matchingContext,
            index,
            indexState.nextRequest,
            matchingContext.lastRequest
        );
    };
}

function startMatchForIndex(
    dispatch,
    getState,
    configuration,
    contextId,
    matchingContext,
    index,
    indexNextRequest,
    contextNextRequest
) {
    const promiseId = uuid();
    dispatch(pageRequested(contextId, index, 0, promiseId, undefined));

    return requestBackendSelectionIfNeeded(
        configuration,
        dispatch,
        contextId,
        index,
        matchingContext
    ).then(backendSelection => {
        const currentlyActivePromiseId = immutable.get(getState(), [
            "matching",
            "contexts",
            contextId,
            "results",
            index.name,
            "pages",
            0,
            "promiseId",
        ]);

        if (currentlyActivePromiseId !== promiseId) {
            return Promise.resolve();
        }

        const finalIndexNextRequest = {...indexNextRequest};

        if (config("matching.excludeExistingMatches")) {
            finalIndexNextRequest.excludedIds = [
                ...finalIndexNextRequest.excludedIds,
                ...backendSelection.getExcludedIds(index.resultType),
            ];
        }

        const matchRequest = {
            ...contextNextRequest,
            ...finalIndexNextRequest,
        };

        const promise = apiCallPromiseForIndex(getState(), contextId, index, {
            matchRequest,
            from: 0,
            size: index.pageSize,
        })
            .then(response => {
                const {matches, ids, count} = convertMatchResponse(response);
                dispatch(pageReceived(contextId, index, 0, promiseId, matches, ids, count));
                requestAnnotationsIfNeeded(dispatch, contextId, index, 0, promiseId, ids);
            })
            .catch(reason => {
                dispatch(pageRequestError(contextId, index, 0, promiseId));
                throw reason;
            });

        dispatch(setIndexLastRequest(contextId, index, finalIndexNextRequest));
        dispatch(pageRequested(contextId, index, 0, promiseId, promise));
        return promise;
    });
}

function requestPage(contextId, index, pageNumber) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const indexResults = objectPath.get(matchingContext, ["results", index.name]);
        const page = objectPath.get(indexResults, ["pages", pageNumber], undefined);

        if (page !== undefined) {
            if (page.state === FetchState.FETCHED) {
                dispatch(setDisplayedPage(contextId, index, pageNumber));
                return page.promise;
            } else if (page.state === FetchState.FETCHING) {
                return page.promise;
            }
        }

        const promiseId = uuid();
        const promise = apiCallPromiseForIndex(getState(), contextId, index, {
            from: pageNumber * index.pageSize,
            size: index.pageSize,
        })
            .then(response => {
                const {matches, ids, count} = convertMatchResponse(response);
                dispatch(
                    pageReceived(contextId, index, pageNumber, promiseId, matches, ids, count)
                );
                requestAnnotationsIfNeeded(dispatch, contextId, index, pageNumber, promiseId, ids);
            })
            .catch(reason => {
                dispatch(pageRequestError(contextId, index, pageNumber, promiseId));
                throw reason;
            });

        dispatch(pageRequested(contextId, index, pageNumber, promiseId, promise));
        return promise;
    };
}

function makeSelection(contextId, index, selection) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const configuration = matchingContext.configurationFn(getState());

        if (!settingSelectionPossible(index, matchingContext)) {
            return;
        }

        const indexResults = matchingContext.results[index.name];
        const backendSelection = indexResults.backendSelection.data;
        const filteredSelection = backendSelection.removeExistingSelectionFrom(selection);

        if (filteredSelection.length > 0) {
            Api.makeSelection(
                index.selectionEndpoints.set,
                matchingContext.matchSourceEntity.id,
                filteredSelection,
                indexResults.selectionStatus
            )
                .then(result => {
                    if (result.hasErrors) {
                        console.error(result.message);
                        throw new Error("Problem setting selection:" + result.message);
                    }

                    if (!gettingSelectionPossible(index, matchingContext)) {
                        return Promise.resolve(BackendSelection.empty());
                    }

                    return fetchBackendSelection(
                        configuration,
                        index,
                        matchingContext.matchSourceEntity.id,
                        0
                    );
                })
                .then(({backendSelection, backendSelectionAsyncPromise}) => {
                    backendSelection = backendSelection.addToExistingSelection(
                        index.resultType,
                        filteredSelection
                    );
                    dispatch(backendSelectionReceived(contextId, index, backendSelection));
                    dispatch(setSelection(contextId, index, []));

                    backendSelectionAsyncPromise.then(backendSelection => {
                        backendSelection = backendSelection.addToExistingSelection(
                            index.resultType,
                            filteredSelection
                        );
                        dispatch(backendSelectionReceived(contextId, index, backendSelection));
                    });
                })
                .catch(error => {
                    dispatch(backendSelectionRequestError(contextId, index));
                    console.error(error);
                });

            dispatch(backendSelectionRequested(contextId, index, true));
        } else {
            dispatch(setSelection(contextId, index, []));
        }
    };
}

function requestBackendSelectionIfNeeded(
    configuration,
    dispatch,
    contextId,
    index,
    matchingContext
) {
    if (!gettingSelectionPossible(index, matchingContext)) {
        return Promise.resolve(BackendSelection.empty());
    }

    backendSelectionRequested(contextId, index, false);

    return fetchBackendSelection(
        configuration,
        index,
        matchingContext.matchSourceEntity.id,
        BACKEND_SELECTION_SPACING_MILLISECONDS
    ).then(({backendSelection, backendSelectionAsyncPromise}) => {
        dispatch(backendSelectionReceived(contextId, index, backendSelection));

        backendSelectionAsyncPromise.then(backendSelection => {
            dispatch(backendSelectionReceived(contextId, index, backendSelection));
        });

        return backendSelection;
    });
}

function requestAnnotationsIfNeeded(dispatch, contextId, index, pageNumber, promiseId, ids) {
    if (index.resultAnnotationEndpoint === undefined) {
        return;
    }

    Api.post(index.resultAnnotationEndpoint, {ids}).then(({response}) => {
        dispatch(
            resultAnnotationsReceived(
                contextId,
                index,
                pageNumber,
                promiseId,
                response.annotationsById
            )
        );
    });
}

function gettingSelectionPossible(index, matchingContext) {
    return (
        index.allowSelection &&
        index.selectionEndpoints &&
        index.selectionEndpoints.get &&
        matchingContext.matchSourceEntity &&
        matchingContext.matchSourceEntity.hasLoaded
    );
}

function settingSelectionPossible(index, matchingContext) {
    return (
        index.allowSelection &&
        index.selectionEndpoints &&
        index.selectionEndpoints.set &&
        matchingContext.matchSourceEntity &&
        matchingContext.matchSourceEntity.hasLoaded
    );
}

function newMatchStarted(contextId, singleIndex) {
    return {
        type: NEW_MATCH_STARTED,
        contextId,
        singleIndex,
    };
}

function setLastRequest(contextId, lastRequest) {
    return {
        type: SET_LAST_REQUEST,
        contextId,
        lastRequest,
    };
}

function setIndexLastRequest(contextId, index, lastRequest) {
    return {
        type: SET_INDEX_LAST_REQUEST,
        contextId,
        index,
        lastRequest,
    };
}

function pageRequested(contextId, index, pageNumber, promiseId, promise) {
    return {
        type: PAGE_REQUESTED,
        contextId,
        index,
        pageNumber,
        promiseId,
        promise,
    };
}

function pageReceived(contextId, index, pageNumber, promiseId, matches, ids, count) {
    return {
        type: PAGE_RECEIVED,
        contextId,
        index,
        pageNumber,
        promiseId,
        matches,
        ids,
        count,
    };
}

function pageRequestError(contextId, index, pageNumber, promiseId) {
    return {
        type: PAGE_REQUEST_ERROR,
        contextId,
        index,
        pageNumber,
        promiseId,
    };
}

function resultAnnotationsReceived(contextId, index, pageNumber, promiseId, annotations) {
    return {
        type: RESULT_ANNOTATIONS_RECEIVED,
        contextId,
        index,
        pageNumber,
        promiseId,
        annotations,
    };
}

function backendSelectionRequested(contextId, index, isMakingSelection) {
    return {
        type: BACKEND_SELECTION_REQUESTED,
        contextId,
        index,
        isMakingSelection,
    };
}

function backendSelectionReceived(contextId, index, data) {
    return {
        type: BACKEND_SELECTION_RECEIVED,
        contextId,
        index,
        data,
    };
}

function backendSelectionRequestError(contextId, index) {
    return {
        type: BACKEND_SELECTION_REQUEST_ERROR,
        contextId,
        index,
    };
}

function setDisplayedPage(contextId, index, pageNumber) {
    return {
        type: SET_DISPLAYED_PAGE,
        contextId,
        index,
        pageNumber,
    };
}

function setMatchSourceEntity(contextId, matchSourceEntity) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const configuration = matchingContext.configurationFn(getState());

        if (!MatchSourceEntity.areSame(matchingContext.matchSourceEntity, matchSourceEntity)) {
            dispatch({
                type: SET_MATCH_SOURCE_ENTITY,
                contextId,
                configuration,
                matchSourceEntity,
            });

            if (matchSourceEntity !== undefined) {
                return matchSourceEntity.documentPromise.then(() => {
                    dispatch({
                        type: MATCH_SOURCE_ENTITY_LOADED,
                        contextId,
                        configuration,
                        matchSourceEntity,
                    });

                    dispatch(newMatch(contextId));
                });
            } else {
                dispatch(clearNextRequest(contextId));
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    };
}

function setQuery(contextId, query) {
    return {
        type: SET_QUERY,
        contextId,
        query,
    };
}

export function setNextRequest(contextId, nextRequest) {
    return {
        type: SET_NEXT_REQUEST,
        contextId,
        nextRequest,
    };
}

function clearNextRequest(contextId, overrides) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const configuration = matchingContext.configurationFn(getState());

        dispatch({
            type: CLEAR_NEXT_REQUEST,
            contextId,
            configuration,
            overrides,
        });
    };
}

function resetNextRequest(contextId) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const configuration = matchingContext.configurationFn(getState());

        dispatch({
            type: RESET_NEXT_REQUEST,
            contextId,
            configuration,
        });
    };
}

function setSelection(contextId, index, selection) {
    return {
        type: SET_SELECTION,
        contextId,
        index,
        selection,
    };
}

function downloadMatchResults(contextId, index) {
    return (dispatch, getState) => {
        const globalState = getState();
        const matchingContext = getMatchingContext(globalState, contextId);
        const indexState = matchingContext.results[index.name];

        return Api.downloadProfileToCandidates({
            candidateIndex: index.name,
            matchRequest: {
                ...matchingContext.lastRequest,
                ...indexState.lastRequest,
            },
            language: globalState.ui.language,
            sortModeGroup: index.sortModeGroup,
            scoreType: index.scoreType,
            expansionType: index.expansionType,
            exportType: index.exportType,
            exportSize: index.exportSize,
        }).then(({response, request}) => {
            const details = ContentDisposition.parse(
                request.getResponseHeader("content-disposition")
            );
            const contentType = request.getResponseHeader("Content-Type");
            const blob = new Blob([response], {type: contentType});
            blob.name = details.parameters.filename;
            const reader = new FileReader();
            reader.onload = e => {
                const anchor = document.createElement("a");
                anchor.style.display = "none";
                anchor.href = e.target.result;
                anchor.download = blob.name;
                anchor.click();
            };
            reader.readAsDataURL(blob);
        });
    };
}

export function setIndexNextRequest(contextId, index, nextRequest) {
    return {
        type: SET_INDEX_NEXT_REQUEST,
        contextId,
        index,
        nextRequest,
    };
}

function setSortMode(contextId, index, sortMode) {
    return {
        type: SET_SORT_MODE,
        contextId,
        index,
        sortMode,
    };
}

function setSelectionStatus(contextId, index, selectionStatus) {
    return {
        type: SET_SELECTION_STATUS,
        contextId,
        index,
        selectionStatus,
    };
}

function updateUiState(contextId, data) {
    return {
        type: UPDATE_UI_STATE,
        contextId,
        data,
    };
}

function selectAllFromApi(contextId, index) {
    return (dispatch, getState) => {
        const matchingContext = getMatchingContext(getState(), contextId);
        const indexState = matchingContext.results[index.name];

        if (!indexState) {
            dispatch(setSelection(contextId, index, []));
            return;
        }

        return apiCallPromiseForIndex(getState(), contextId, index, {
            from: 0,
            size: index.selectAllLimit,
            _source: false,
        })
            .then(response => {
                const {ids} = convertMatchResponse(response);
                dispatch(setSelection(contextId, index, ids));
            })
            .catch(reason => {
                dispatch(setSelection(contextId, index, []));
                throw reason;
            });
    };
}

function apiCallPromiseForIndex(globalState, contextId, index, overrides) {
    const matchingContext = getMatchingContext(globalState, contextId);
    const indexState = matchingContext.results[index.name];

    return apiCallFunctionForIndex(index)({
        index: index.name,
        matchingStrategy: index.matchingStrategyName,
        matchRequest: {
            ...matchingContext.lastRequest,
            ...indexState.lastRequest,
        },
        from: 0,
        size: 0,
        language: globalState.ui.language,
        sortModeGroup: index.sortModeGroup,
        scoreType: index.scoreType,
        expansionType: index.expansionType,
        conversionPreProcessor: index.apiConversionPreProcessor,
        ...overrides,
    });
}

function apiCallFunctionForIndex(index) {
    if (index.indexDocumentType === IndexDocumentType.JOB_PROFILE) {
        return Api.matchProfileToJobsV1.bind(Api);
    } else if (index.indexDocumentType === IndexDocumentType.CANDIDATE_PROFILE) {
        return Api.matchProfileToCandidatesV1.bind(Api);
    }

    const apiVerison = config("api.version");

    if (apiVerison.gte("2.2.0")) {
        switch (index.resultType) {
            case ProfileType.JOB:
                return Api.matchProfileToJobsV2.bind(Api);

            case ProfileType.CANDIDATE:
                return Api.matchProfileToCandidatesV2.bind(Api);

            default:
                throw new Error(`Unsupported result type: ${index.resultType}`);
        }
    } else {
        switch (index.resultType) {
            case ProfileType.JOB:
                return Api.matchProfileToJobsV1.bind(Api);

            case ProfileType.CANDIDATE:
                return Api.matchProfileToCandidatesV1.bind(Api);

            default:
                throw new Error(`Unsupported result type: ${index.resultType}`);
        }
    }
}

function convertMatchResponse({matches, metadata}) {
    const matchesById = {};
    const ids = [];

    for (const match of matches) {
        matchesById[match.id] = match;
        ids.push(match.id);
    }

    return {
        matches: matchesById,
        ids,
        count: metadata.count,
    };
}

function getMatchingContext(state, contextId) {
    const matchingContext = state.matching.contexts[contextId];

    if (matchingContext === undefined) {
        throw new Error(`A matching context with ID ${contextId} doesn't exist.`);
    }

    return matchingContext;
}
